import React, { useEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import Hotjar from "../../components/Hotjar";
import { Linkedin } from "../../components/Analytics/Linkedin";
import StripePricingTable from "../../components/StripePricingTable";
import FreeTrialButton from "../../components/FreeTrialButton";
import ArrowRight from "../../components/Icons/ArrowRightIcon";
import CardPanel from "../../components/DesignSystem/Output/CardPanel";
import {
	JobCardData,
	APPLIED,
	CONFIRMED,
	RECRUITERINTERVIEW,
	HIRINGMANAGERINTERVIEW,
	ValidateReferralResponse
} from "../../DataModels/DataModels";
import { useValidateReferralCode } from "../../services/api-client/useReferrals";
import ReferralBanner from "../../components/DesignSystem/Output/ReferralBanner";

// Fictional company job data
const demoJobs: JobCardData[] = [
	{
		id: "1",
		title: "Senior Software Engineer",
		company: "Hooli",
		state: APPLIED,
		creationTimestamp: new Date(
			Date.now() - 2 * 24 * 60 * 60 * 1000
		).toISOString(),
		isError: false,
		isPending: false
	},
	{
		id: "2",
		title: "AI Research Scientist",
		company: "Stark Industries",
		state: APPLIED,
		creationTimestamp: new Date(
			Date.now() - 5 * 24 * 60 * 60 * 1000
		).toISOString(),
		isError: false,
		isPending: false
	},
	{
		id: "3",
		title: "Special Project Chemist",
		company: "Los Pollos Hermanos",
		state: APPLIED,
		creationTimestamp: new Date(
			Date.now() - 7 * 24 * 60 * 60 * 1000
		).toISOString(),
		isError: false,
		isPending: false
	},
	{
		id: "4",
		title: "Food Science Researcher",
		company: "Soylent Industries",
		state: CONFIRMED,
		creationTimestamp: new Date(
			Date.now() - 10 * 24 * 60 * 60 * 1000
		).toISOString(),
		isError: false,
		isPending: false
	},
	{
		id: "5",
		title: "Testing Specialist",
		company: "Aperture Science",
		state: RECRUITERINTERVIEW,
		creationTimestamp: new Date(
			Date.now() - 14 * 24 * 60 * 60 * 1000
		).toISOString(),
		isError: false,
		isPending: false
	},
	{
		id: "6",
		title: "Robotics Engineer",
		company: "Cyberdyne Systems",
		state: RECRUITERINTERVIEW,
		creationTimestamp: new Date(
			Date.now() - 3 * 24 * 60 * 60 * 1000
		).toISOString(),
		isError: false,
		isPending: false
	},
	{
		id: "7",
		title: "AI Ethics Consultant",
		company: "Tyrell Corporation",
		state: HIRINGMANAGERINTERVIEW,
		creationTimestamp: new Date(
			Date.now() - 6 * 24 * 60 * 60 * 1000
		).toISOString(),
		isError: false,
		isPending: false
	},
	{
		id: "8",
		title: "Product Designer",
		company: "ACME",
		state: APPLIED,
		creationTimestamp: new Date(
			Date.now() - 1 * 24 * 60 * 60 * 1000
		).toISOString(),
		isError: false,
		isPending: false
	},
	{
		id: "9",
		title: "Full Stack Developer",
		company: "Pied Piper",
		state: CONFIRMED,
		creationTimestamp: new Date(
			Date.now() - 4 * 24 * 60 * 60 * 1000
		).toISOString(),
		isError: false,
		isPending: false
	}
];

const MarketingContent: React.FC = () => (
	<>
		{/* Hero Section */}
		<div className="text-center">
			<h1 className="text-4xl font-bold text-primary mb-4">
				Never Miss a Job Application Update
			</h1>
			<p className="text-xl text-primary-active max-w-3xl mx-auto">
				Track every application, interview, and follow-up in one organized
				dashboard.
			</p>
		</div>

		{/* CTA Section */}
		<div className="bg-primary-accent rounded-xl shadow-lg p-8 text-center border border-primary-inactive">
			<h2 className="text-3xl font-bold text-primary mb-4">
				Stay Organized, Get Hired
			</h2>
			<p className="text-xl text-primary-active mb-6">
				Start tracking your job applications today
			</p>
			<FreeTrialButton
				className="bg-primary-active text-primary-background font-semibold px-6 py-3 rounded-lg"
				gaEventName="startjobtracking"
				gaLocation="cta"
			>
				Start job tracking
			</FreeTrialButton>
		</div>

		{/* Application Tracking with CardPanel */}
		<div className="rounded-xl border border-primary-inactive shadow-lg p-8">
			<h2 className="text-2xl font-bold text-primary mb-6">
				Application Tracking
			</h2>
			<div className="space-y-6 ">
				<CardPanel jobs={demoJobs} isDemo={true} />
			</div>
		</div>

		{/* AI Powered Title */}
		<div className="text-center">
			<h1 className="text-4xl font-bold text-primary mb-4">
				AI-Powered Résumé Writer
			</h1>
			<p className="text-xl max-w-2xl mx-auto">
				Use the most intelligent models to build insights into your résumé
			</p>
		</div>

		{/*AI Features*/}
		<div className="bg-primary-accent rounded-xl shadow-lg p-8">
			<div className="grid md:grid-cols-2 gap-8">
				<div className="space-y-4">
					<h2 className="text-2xl font-bold text-primary">AI Capabilities</h2>

					<div className="flex items-start gap-4">
						<div className="w-10 h-10 bg-primary-background rounded-lg flex items-center justify-center">
							<span className="material-symbols-outlined text-primary-active">
								insights
							</span>
						</div>
						<div>
							<h3 className="font-semibold text-primary">Skill Analysis</h3>
							<p className="text-primary-active">
								Identify skills for target positions
							</p>
						</div>
					</div>
					<div className="flex items-start gap-4">
						<div className="w-10 h-10 bg-primary-background rounded-lg flex items-center justify-center">
							<span className="material-symbols-outlined text-primary-active">
								visibility
							</span>
						</div>
						<div>
							<h3 className="font-semibold text-primary">ATS Optimization</h3>
							<p className="text-primary-active">
								Ensure compatibility with applicant tracking systems
							</p>
						</div>
					</div>
					<div className="flex items-start gap-4">
						<div className="w-10 h-10 bg-primary-background rounded-lg flex items-center justify-center">
							<span className="material-symbols-outlined text-primary-active">
								trending_up
							</span>
						</div>
						<div>
							<h3 className="font-semibold text-primary">Industry Insights</h3>
							<p className="text-primary-active">
								Tailored recommendations based on industry standards
							</p>
						</div>
					</div>
				</div>

				<div className="space-y-4">
					<h2 className="text-2xl font-bold text-primary">Résumé Score</h2>

					<div>
						<div className="flex justify-between mb-1 text-primary">
							<span>Content Quality</span>
							<span>95%</span>
						</div>
						<div className="w-full bg-primary-background rounded-full h-2">
							<div
								className="bg-primary-active h-2 rounded-full"
								style={{ width: "95%" }}
							></div>
						</div>
					</div>
					<div>
						<div className="flex justify-between mb-1 text-primary">
							<span>Keyword Match</span>
							<span>82%</span>
						</div>
						<div className="w-full bg-primary-background rounded-full h-2">
							<div
								className="bg-primary-active h-2 rounded-full"
								style={{ width: "82%" }}
							></div>
						</div>
					</div>
					<div>
						<div className="flex justify-between mb-1 text-primary">
							<span>ATS Compatibility</span>
							<span>88%</span>
						</div>
						<div className="w-full bg-primary-background rounded-full h-2">
							<div
								className="bg-primary-active h-2 rounded-full"
								style={{ width: "88%" }}
							></div>
						</div>
					</div>
				</div>
			</div>
		</div>

		{/* Craft Hero Section */}
		<div className="text-center">
			<h1 className="text-4xl font-bold text-primary mb-4">
				Craft Perfect Cover Letters in Minutes
			</h1>
			<p className="text-xl max-w-2xl mx-auto">
				Our AI-powered assistant helps you create personalized, professional
				cover letters that catch employers' attention.
			</p>
		</div>

		{/* Craft Key Features  */}
		<div className="grid md:grid-cols-2 gap-8">
			<div className="bg-primary-accent rounded-xl shadow-lg p-8">
				<h2 className="text-2xl font-bold text-primary mb-6">Key Features</h2>
				<ul className="space-y-4">
					<li className="flex items-start">
						<ArrowRight className="w-5 h-5 text-primary-active mt-1 mr-2" />
						<div>
							<h3 className="font-semibold text-primary">
								AI-Powered Personalization
							</h3>
							<p className="">
								Tailored content that matches job requirements and company
								culture
							</p>
						</div>
					</li>
					<li className="flex items-start">
						<ArrowRight className="w-5 h-5 text-primary-active mt-1 mr-2" />
						<div>
							<h3 className="font-semibold text-primary">
								Industry-Specific Text
							</h3>
							<p className="">
								Professional language optimized for your industry
							</p>
						</div>
					</li>
					<li className="flex items-start">
						<ArrowRight className="w-5 h-5 text-primary-active mt-1 mr-2" />
						<div>
							<h3 className="font-semibold text-primary">
								Keyword Optimization
							</h3>
							<p className="">
								Strategic placement of relevant keywords for ATS systems
							</p>
						</div>
					</li>
				</ul>
			</div>

			<div className="bg-primary-accent rounded-xl shadow-lg p-8">
				<h2 className="text-2xl font-bold text-primary mb-6">Benefits</h2>
				<ul className="space-y-4">
					<li className="flex items-start">
						<ArrowRight className="w-5 h-5 text-primary-active mt-1 mr-2" />
						<div>
							<h3 className="font-semibold text-primary">Save Time</h3>
							<p className="">
								Create custom cover letters in moments instead of hours
							</p>
						</div>
					</li>
					<li className="flex items-start">
						<ArrowRight className="w-5 h-5 text-primary-active mt-1 mr-2" />
						<div>
							<h3 className="font-semibold text-primary">
								Higher Response Rate
							</h3>
							<p className="">Increase your chances of getting interviews</p>
						</div>
					</li>
					<li className="flex items-start">
						<ArrowRight className="w-5 h-5 text-primary-active mt-1 mr-2" />
						<div>
							<h3 className="font-semibold text-primary">
								Professional Quality
							</h3>
							<p className="">
								Polished, error-free writing that impresses employers
							</p>
						</div>
					</li>
				</ul>
			</div>
		</div>
	</>
);

const LandingMain: React.FC = () => {
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const refCode = searchParams.get("ref");

	// Validate referral code (public, no auth required)
	const { data: referralInfo, isLoading: referralLoading } =
		useValidateReferralCode(refCode);

	// Show banner from API response, or a loading placeholder while in-flight
	const bannerInfo: ValidateReferralResponse | null = referralInfo ?? null;
	const showBannerLoading = !!refCode && referralLoading;

	// Store referral code in sessionStorage for the login page — only if the code is valid
	useEffect(() => {
		try {
			if (refCode && referralInfo?.valid) {
				sessionStorage.setItem("referral_code", refCode);
				sessionStorage.setItem("referral_info", JSON.stringify(referralInfo));
			}
		} catch {
			/* no sessionStorage in test env */
		}
	}, [refCode, referralInfo]);

	useEffect(() => {
		// Handle hash navigation on component mount and when hash changes
		const hash = location.hash;
		if (hash) {
			// Remove the '#' and find the element
			const id = hash.substring(1);
			const element = document.getElementById(id);
			if (element) {
				// Scroll to the element with a longer delay to allow content to load
				setTimeout(() => {
					element.scrollIntoView({ behavior: "smooth", block: "start" });
				}, 500);
			}
		}
	}, [location.hash]);

	// Minimal layout when referral code is present (PRD: banner + pricing only)
	const isReferralVisit = !!refCode;

	return (
		<main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 bg-primary-background min-h-screen">
			<div className="space-y-12">
				{/* Referral Banner */}
				{showBannerLoading ? (
					<div className="border border-primary-inactive rounded-xl p-4 mb-4 bg-primary-accent animate-pulse">
						<div className="flex items-center gap-3">
							<div className="h-6 w-6 bg-primary-inactive rounded-full flex-shrink-0" />
							<div className="space-y-2 flex-grow">
								<div className="h-4 bg-primary-inactive rounded w-48" />
								<div className="h-3 bg-primary-inactive rounded w-64" />
							</div>
						</div>
					</div>
				) : (
					bannerInfo && <ReferralBanner referralInfo={bannerInfo} />
				)}

				{isReferralVisit ? (
					<details className="group">
						<summary className="cursor-pointer text-primary text-sm hover:text-primary-active transition-colors list-none flex items-center justify-center gap-2">
							<span className="material-symbols-outlined text-base group-open:rotate-90 transition-transform">
								chevron_right
							</span>
							Learn more about JobMap
						</summary>
						<div className="space-y-12 mt-8">
							<MarketingContent />
						</div>
					</details>
				) : (
					<MarketingContent />
				)}

				{/* Main Pricing Section */}
				<div id="pricing" className="space-y-6">
					{/* Pricing Hero */}
					<div className="text-center">
						<h1 className="text-4xl font-bold text-primary mb-2">
							Simple, Transparent Pricing
						</h1>
						<p className="text-lg font-semibold max-w-2xl mx-auto">
							We're here to support your job search success
						</p>
					</div>

					{/*
					TO Edit Pricing Table: In the stripe Dashboard, go to Product catalog > pricing tables.
					Edit the JobMap Essentials (in production) - Which there is a product of the same name.
					This will update this object with zero dev work.
					*/}

					{/* Pricing Cards Row */}
					<div className="grid md:grid-cols-2 md:grid-rows-[auto_auto] gap-8 md:gap-x-8 md:gap-y-0 font-stripeMatch">
						{/* Free Trial Card */}
						<div
							className={
								isReferralVisit && bannerInfo && bannerInfo.valid
									? "order-2 md:order-1 md:row-span-2 md:grid md:grid-rows-subgrid"
									: ""
							}
						>
							{/* Spacer — occupies row 1 on desktop, height matches heading box automatically via subgrid */}
							{isReferralVisit && bannerInfo && bannerInfo.valid && (
								<div className="hidden md:block" />
							)}
							<div className="rounded-xl shadow-lg p-8 border border-primary-inactive flex flex-col items-center pt-32 h-full">
								<div className="w-[230px] pt-2">
									{/* Badge */}
									<div className="flex mb-2">
										<span className="bg-yellow-600 text-primary-background text-xs font-bold px-3 py-1 rounded uppercase">
											Free Tier
										</span>
									</div>

									{/* Title */}
									<h2 className="text-xl font-bold text-primary mb-9">
										Start Free Trial
									</h2>

									{/* Price */}
									<div className="mb-4">
										<span className="text-4xl font-bold text-primary">
											$0.00
										</span>
										<span className="text-primary-active text-sm ml-2 opacity-70">
											forever
										</span>
									</div>

									{/* CTA Button */}
									<FreeTrialButton
										className="flex flex-col text-center justify-center rounded-full bg-primary-active font-[500] text-primary-background w-full h-[44px]"
										gaEventName="FreeTrial"
										gaLocation="pricing"
									>
										Start free trial
									</FreeTrialButton>

									{/* Features List */}
									<div className="flex-1 mt-3 text-sm">
										<p className="text-primary mb-2">This includes:</p>
										<ul className="space-y-2">
											<li className="flex items-start">
												<svg
													className="w-4 h-4 text-primary-active mt-0.5 mr-3 flex-shrink-0 opacity-70"
													fill="currentColor"
													viewBox="0 0 20 20"
												>
													<path
														fillRule="evenodd"
														d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
														clipRule="evenodd"
													/>
												</svg>
												<span className="text-primary">
													Only 3 AI written cover letters
												</span>
											</li>
											<li className="flex items-start">
												<svg
													className="w-4 h-4 text-primary-active mt-0.5 mr-3 flex-shrink-0 opacity-70"
													fill="currentColor"
													viewBox="0 0 20 20"
												>
													<path
														fillRule="evenodd"
														d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
														clipRule="evenodd"
													/>
												</svg>
												<span className="text-primary">
													Only 3 AI written résumés
												</span>
											</li>
											<li className="flex items-start">
												<svg
													className="w-4 h-4 text-primary-active mt-0.5 mr-3 flex-shrink-0 opacity-70"
													fill="currentColor"
													viewBox="0 0 20 20"
												>
													<path
														fillRule="evenodd"
														d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
														clipRule="evenodd"
													/>
												</svg>
												<span className="text-primary">
													Smart job application tracking
												</span>
											</li>
											<li className="flex items-start">
												<svg
													className="w-4 h-4 text-primary-active mt-0.5 mr-3 flex-shrink-0 opacity-70"
													fill="currentColor"
													viewBox="0 0 20 20"
												>
													<path
														fillRule="evenodd"
														d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
														clipRule="evenodd"
													/>
												</svg>
												<span className="text-primary">Basic support</span>
											</li>
										</ul>
									</div>
								</div>
							</div>
						</div>

						{/* Stripe Pricing Table Card */}
						{isReferralVisit && bannerInfo && bannerInfo.valid ? (
							<div className="order-1 md:order-2 rounded-xl bg-white border border-white md:row-span-2 md:grid md:grid-rows-subgrid">
								{bannerInfo.referralType === "group" ? (
									<div className="px-3 py-2 text-center md:py-4 md:px-7 flex items-center justify-center">
										<p className="text-base md:text-lg font-semibold text-primary-background">
											Earn bonus credits when you subscribe
										</p>
									</div>
								) : (
									<div className="px-6 py-3 text-center md:py-5">
										<h2 className="text-lg font-semibold text-primary-background">
											With Your Referral
										</h2>
										<div className="flex items-center justify-center gap-8 mt-2">
											<span className="flex items-center gap-1.5 text-sm text-primary-inactive">
												<span className="material-symbols-outlined text-base text-green-500">
													auto_awesome
												</span>
												Bonus credits for you
											</span>
											<span className="flex items-center gap-1.5 text-sm text-primary-inactive">
												<span className="material-symbols-outlined text-base text-green-500">
													auto_awesome
												</span>
												Bonus credits for {bannerInfo.originatorName}
											</span>
										</div>
									</div>
								)}
								<div className="rounded-xl shadow-lg p-8 bg-primary-background">
									<StripePricingTable theme="dark" />
								</div>
							</div>
						) : (
							<div className="rounded-xl shadow-lg p-8 border border-primary-inactive">
								<StripePricingTable theme="dark" />
							</div>
						)}
					</div>

					{/* User Journey -
					 *     0. User has just clicked on a Advertisement that has linked them from Linkedin, X, Ticktok, Etc.
					 *     1. They land on this page and have decide to purchase  Therefore clicking the stripe purchase button
					 *     2. Which navigates them to the Stripe Checkout page. (each stripe product has a return URL)
					 *     3. They shall return to the Login Page with the stripe checkout session id =>  ./login?session_id={CHECKOUT_SESSION_ID}
					 *         A. The presence of the session_id, puts the login page into account creation mode.
					 * 	5. User creates an account by binding it to the prodvider, and connecting the session_id to the new UserID.
					 * 	6. User is forwarded to the (high sec) activation page AS a First time user experiance (FTUE) - ./activation?session_id={CHECKOUT_SESSION_ID}
					 *     7. User continues to ./DND as normal
					 */}
				</div>
			</div>
		</main>
	);
};

function LandingPageV2() {
	return (
		<>
			<Hotjar />
			<Linkedin />
			<LandingMain />
		</>
	);
}

export default LandingPageV2;
